import { useCallback, useMemo } from 'react'
import {
  collection,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { addDoc } from '../lib/firestore'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { runMutation } from '../lib/firestoreMutation'
import { track } from '../lib/analytics'
import type { Group } from '../types'
import { generateInviteCode } from '@shared/groupJoin'
import { fbt } from 'fbtee'

export function useGroups(userId: string | null) {
  const memberRef = userId
    ? (query(collection(db, 'groups'), where('memberIds', 'array-contains', userId)) as Query<Group>)
    : null
  const guestRef = userId
    ? (query(collection(db, 'groups'), where('guestIds', 'array-contains', userId)) as Query<Group>)
    : null
  const { data: memberGroups, loading: memberLoading, error: memberError } = useFirestoreCollection<Group>(memberRef)
  const { data: guestGroups, loading: guestLoading, error: guestError } = useFirestoreCollection<Group>(guestRef)

  const data = useMemo(
    () => [...memberGroups, ...guestGroups.filter(g => !memberGroups.some(m => m.id === g.id))],
    [memberGroups, guestGroups],
  )
  const loading = memberLoading || guestLoading
  const error = memberError ?? guestError ?? null

  const createGroup = useCallback(
    async (name: string, imageUrl?: string): Promise<string> => {
      if (!userId) return ''
      return runMutation(fbt("Couldn't create group", 'Error toast when creating a group fails'), async () => {
        const ref = await addDoc(collection(db, 'groups'), {
          name,
          inviteCode: generateInviteCode(),
          createdBy: userId,
          memberIds: [userId],
          createdAt: serverTimestamp(),
          ...(imageUrl ? { imageUrl } : {}),
        })
        track('group_created', { group_id: ref.id })
        return ref.id
      })
    },
    [userId],
  )

  const groupIds = useMemo(() => data.map(g => g.id), [data])

  return { groups: data, groupIds, loading, error, createGroup }
}
